import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, Award, ChevronLeft, ChevronRight, ExternalLink, GraduationCap,
  Handshake, Home, MapPinned, Medal, Megaphone, MoreHorizontal, Rocket, Trophy, Users,
} from 'lucide-react';
import SEO from '../components/SEO';
import { fetchPrestasiMahasiswa, getImageUrl } from '../utils/api';

const PAGE_SIZE = 10; // 5 baris kartu (2 kolom)

/// Pagination bergaya profesional: nomor halaman + elipsis saat halaman
/// banyak, selalu menampilkan halaman pertama/terakhir & tetangga halaman
/// aktif. Disembunyikan otomatis bila cuma 1 halaman.
function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;

  const pages = [];
  const addPage = (p) => pages.push(p);
  const addEllipsis = () => pages.push('…');

  addPage(1);
  if (page > 3) addEllipsis();
  for (let p = Math.max(2, page - 1); p <= Math.min(totalPages - 1, page + 1); p += 1) addPage(p);
  if (page < totalPages - 2) addEllipsis();
  if (totalPages > 1) addPage(totalPages);

  const btnBase = 'grid h-9 w-9 place-items-center rounded-xl text-sm font-semibold transition';

  return (
    <nav aria-label="Navigasi halaman" className="mt-10 flex items-center justify-center gap-1.5">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        aria-label="Halaman sebelumnya"
        className={`${btnBase} border border-gray-200 bg-white text-gray-500 hover:border-purple-200 hover:text-[#7B2FBE] disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-[#1A182E] dark:text-gray-400`}
      >
        <ChevronLeft size={16} />
      </button>
      {pages.map((p, i) => (p === '…' ? (
        <span key={`ellipsis-${i}`} className="grid h-9 w-9 place-items-center text-gray-400"><MoreHorizontal size={16} /></span>
      ) : (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          aria-current={p === page ? 'page' : undefined}
          className={`${btnBase} ${p === page
            ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
            : 'border border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:text-[#7B2FBE] dark:border-gray-700 dark:bg-[#1A182E] dark:text-gray-300'}`}
        >
          {p}
        </button>
      )))}
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        aria-label="Halaman berikutnya"
        className={`${btnBase} border border-gray-200 bg-white text-gray-500 hover:border-purple-200 hover:text-[#7B2FBE] disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-[#1A182E] dark:text-gray-400`}
      >
        <ChevronRight size={16} />
      </button>
    </nav>
  );
}

const CATEGORY_LABELS = {
  penalaran: 'Penalaran & Inovasi',
  seni: 'Seni & Budaya',
  kompetisi: 'Kompetisi',
  kepemimpinan: 'Kepemimpinan',
  lainnya: 'Lainnya',
};

const CATEGORY_FILTERS = [
  { key: 'semua', label: 'Semua' },
  { key: 'penalaran', label: 'Penalaran & Inovasi' },
  { key: 'seni', label: 'Seni & Budaya' },
  { key: 'lainnya', label: 'Lainnya' },
];

const matchesCategoryFilter = (item, filter) => {
  if (filter === 'semua') return true;
  if (filter === 'lainnya') return !['penalaran', 'seni'].includes(item.category);
  return item.category === filter;
};

const CATEGORY_STYLES = {
  penalaran: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  seni: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  kompetisi: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  kepemimpinan: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  lainnya: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
};

const PrestasiMahasiswa = () => {
  const [achievements, setAchievements] = useState([]);
  const [activeCategory, setActiveCategory] = useState('semua');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const listTopRef = useRef(null);

  useEffect(() => {
    let active = true;
    fetchPrestasiMahasiswa()
      .then((data) => { if (active) setAchievements(Array.isArray(data) ? data : []); })
      .catch(() => { if (active) setAchievements([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const visibleAchievements = achievements.filter((item) => matchesCategoryFilter(item, activeCategory));
  const nationalCount = achievements.filter((item) => item.level === 'Nasional').length;
  const regionalCount = achievements.filter((item) => item.level === 'Kota/Provinsi').length;

  const totalPages = Math.max(1, Math.ceil(visibleAchievements.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageAchievements = useMemo(
    () => visibleAchievements.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [visibleAchievements, currentPage],
  );

  const changeCategory = (category) => {
    setActiveCategory(category);
    setPage(1);
  };
  const changePage = (next) => {
    setPage(next);
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div data-testid="prestasi-mahasiswa-page">
      <SEO
        title="Prestasi Mahasiswa"
        description="Dokumentasi prestasi dan capaian mahasiswa STIKES Harapan Ibu Jambi."
      />

      <div className="page-hero pt-[70px]">
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm text-white/70">
              <Link to="/" className="flex items-center gap-1 transition-colors hover:text-white"><Home size={14} /> Beranda</Link>
              <ChevronRight size={14} />
              <Link to="/unit/kemahasiswaan" className="transition-colors hover:text-white">Kemahasiswaan &amp; Alumni</Link>
              <ChevronRight size={14} />
              <span className="text-white">Prestasi Mahasiswa</span>
            </div>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-purple-100">
              <Award size={14} /> Ruang tumbuh dan berprestasi
            </span>
            <h1 className="mt-5 font-heading text-4xl font-bold text-white md:text-5xl">Prestasi Mahasiswa</h1>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/80">
              Dokumentasi capaian mahasiswa STIKES Harapan Ibu Jambi di bidang akademik, seni, olahraga, inovasi,
              organisasi, dan kompetisi.
            </p>
          </motion.div>
        </div>
      </div>

      <main className="bg-[#F8F7FF] dark:bg-[#121020]">
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="grid gap-4 sm:grid-cols-3">
              {[
                { value: achievements.length, label: 'Capaian tercatat', icon: Trophy },
                { value: nationalCount, label: 'Tingkat nasional', icon: Medal },
                { value: regionalCount, label: 'Capaian daerah', icon: MapPinned },
              ].map(({ value, label, icon: Icon }, index) => (
                <motion.div key={label} initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} className="rounded-2xl border border-purple-100 bg-white p-5 shadow-sm dark:border-purple-900/30 dark:bg-[#1A182E]">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-purple-100 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300"><Icon size={19} /></span>
                  <p className="mt-4 font-heading text-2xl font-bold text-[#1A1A2E] dark:text-white">{value}</p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{label}</p>
                </motion.div>
              ))}
            </div>

            <div ref={listTopRef} className="mt-16 flex scroll-mt-24 flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7B2FBE] dark:text-purple-300">Capaian mahasiswa</span>
                <h2 className="mt-3 font-heading text-3xl font-bold text-[#1A1A2E] dark:text-white">Prestasi yang menginspirasi</h2>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-gray-500 dark:text-gray-400">Data prestasi dikelola dan diperbarui melalui Panel Admin Kemahasiswaan.</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_FILTERS.map((filter) => (
                  <button key={filter.key} type="button" onClick={() => changeCategory(filter.key)} className={`rounded-full px-3.5 py-2 text-xs font-semibold transition ${activeCategory === filter.key ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20' : 'border border-gray-200 bg-white text-gray-600 hover:border-purple-200 hover:text-[#7B2FBE] dark:border-gray-700 dark:bg-[#1A182E] dark:text-gray-300'}`}>
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? <div className="grid min-h-48 place-items-center text-sm text-gray-400">Memuat prestasi…</div> : visibleAchievements.length === 0 ? (
              <div className="mt-7 rounded-3xl border border-dashed border-purple-200 bg-white px-6 py-16 text-center dark:border-purple-900/40 dark:bg-[#1A182E]">
                <Award className="mx-auto text-purple-300 dark:text-purple-700" size={42} />
                <h3 className="mt-4 font-heading text-lg font-bold text-[#1A1A2E] dark:text-white">Belum ada prestasi yang ditampilkan</h3>
                <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-500 dark:text-gray-400">Data prestasi mahasiswa akan tampil di halaman ini setelah dikelola dan dipublikasikan melalui Panel Admin.</p>
                <Link to="/unit/kemahasiswaan" className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#7B2FBE] hover:underline dark:text-purple-300">Ke halaman Kemahasiswaan <ArrowRight size={15} /></Link>
              </div>
            ) : (
              <div className="mt-7 grid gap-5 lg:grid-cols-2">
                {pageAchievements.map((item, index) => (
                  <motion.article key={item.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.05 }} className="group overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg dark:border-gray-800 dark:bg-[#1A182E]">
                    <div className="flex items-start gap-4 p-4 sm:gap-5 sm:p-5">
                      <Link to={`/prestasi-mahasiswa/${item.slug || item.id}`} className="relative block w-20 shrink-0 overflow-hidden rounded-2xl bg-purple-50 shadow-sm ring-1 ring-black/5 aspect-[3/4] dark:bg-purple-900/20 sm:w-28">
                        {item.imageUrl
                          ? <img src={getImageUrl(item.imageUrl, { width: 480 })} alt={item.studentName} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" style={{ objectPosition: '50% 60%' }} />
                          : <span className={`flex h-full w-full items-center justify-center ${CATEGORY_STYLES[item.category] || CATEGORY_STYLES.lainnya}`}><Award size={24} /></span>}
                      </Link>
                      <div className="flex min-w-0 flex-1 flex-col gap-2.5 py-1">
                        <div className="flex flex-wrap gap-2 text-[10px] font-bold uppercase tracking-wide"><span className="rounded-full bg-gray-100 px-3 py-1 text-gray-500 dark:bg-white/10 dark:text-gray-400">{CATEGORY_LABELS[item.category] || item.category}</span>{item.year && <span className="rounded-full bg-purple-50 px-3 py-1 text-[#7B2FBE] dark:bg-purple-900/30 dark:text-purple-300">{item.year}</span>}</div>
                        <h3 className="font-heading text-lg font-bold leading-snug text-[#1A1A2E] dark:text-white sm:text-xl">
                          <Link to={`/prestasi-mahasiswa/${item.slug || item.id}`} className="transition-colors hover:text-[#7B2FBE] dark:hover:text-purple-300">{item.title}</Link>
                        </h3>
                        <p className="min-h-[7.5rem] line-clamp-5 whitespace-pre-line break-words text-sm leading-relaxed text-gray-600 dark:text-gray-400">{item.description}</p>
                        <div className="mt-auto grid gap-2 border-t border-gray-100 pt-3 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400 sm:grid-cols-2">
                          <span className="flex items-center gap-2"><GraduationCap size={14} className="text-[#7B2FBE]" /> {item.studentName}</span>
                          <span className="flex items-center gap-2"><Medal size={14} className="text-[#7B2FBE]" /> {item.level}</span>
                          {item.programStudy && <span className="flex items-center gap-2"><Users size={14} className="text-[#7B2FBE]" /> {item.programStudy}</span>}
                          {item.organizer && <span className="flex items-center gap-2"><Handshake size={14} className="text-[#7B2FBE]" /> {item.organizer}</span>}
                        </div>
                        {item.link && <a href={item.link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#7B2FBE] hover:underline dark:text-purple-300">Lihat dokumentasi <ExternalLink size={13} /></a>}
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
            <Pagination page={currentPage} totalPages={totalPages} onChange={changePage} />
          </div>
        </section>

        <section className="bg-white py-16 dark:bg-[#100E1C]">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#4C1D95] via-[#7B2FBE] to-[#9333EA] p-7 text-white shadow-xl shadow-purple-900/10 sm:p-10">
              <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10" />
              <div className="relative z-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
                <div className="max-w-2xl"><div className="flex items-center gap-2 text-sm font-semibold text-purple-100"><Rocket size={18} /> Punya capaian atau ingin ikut kompetisi?</div><h2 className="mt-3 font-heading text-2xl font-bold sm:text-3xl">Mulai dari Unit Kemahasiswaan.</h2><p className="mt-3 leading-relaxed text-purple-100">Sampaikan minat, ide, atau prestasimu agar mendapat arahan pembina dan informasi program yang sesuai.</p></div>
                <Link to="/unit/kemahasiswaan" className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-[#6D28A8] shadow-lg transition hover:-translate-y-0.5 hover:bg-purple-50">Buka Kemahasiswaan <ArrowRight size={16} /></Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PrestasiMahasiswa;
